import React from 'react';
import { View, Text } from 'react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import {
  labelThemeColor,
  evidenceThemeColor,
  capitalizeLabel,
  confidenceBandLabel,
  confidenceBandColor,
  type EvidenceLabel,
} from '@/lib/scoring/score-display';
import type { VentureLabel } from '@/lib/scoring/methodology';

export interface RiskSummary {
  high: number;
  medium: number;
  low: number;
}

interface SubScoreRowsProps {
  ventureLabel?: VentureLabel | string;
  ventureScore?: number;
  evidenceLabel?: EvidenceLabel;
  /** Aggregate confidence 0-100 (preferred over evidenceLabel for scorecard context) */
  evidenceConfidence?: number;
  /** Optional source-mix flavor tagline (e.g. "mostly verified") */
  evidenceSourceFlavor?: string;
  dealReadinessPercent?: number;
  dealReadinessPresent?: number;
  dealReadinessTotal?: number;
  riskSummary?: RiskSummary | string;
  density?: 'compact' | 'default';
  /** When true, use abbreviated labels suited for narrow inline columns */
  abbreviatedLabels?: boolean;
  /** Stacked: label small-caps above value. Inline: label and value on same row. */
  layout?: 'inline' | 'stacked';
}

interface RowProps {
  label: string;
  children: React.ReactNode;
  density: 'compact' | 'default';
  layout: 'inline' | 'stacked';
  colors: any;
}

function Row({ label, children, density, layout, colors }: RowProps) {
  const padY = density === 'compact' ? 4 : 7;
  const isCompactInline = layout === 'inline' && density === 'compact';
  const labelFont = isCompactInline ? 10.5 : 12;

  if (layout === 'stacked') {
    return (
      <View style={{ paddingVertical: padY }}>
        <Text
          style={{
            color: colors.text.tertiary,
            fontSize: 9,
            fontWeight: '700',
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            marginBottom: 2,
          }}
          numberOfLines={1}
        >
          {label}
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {children}
        </View>
      </View>
    );
  }

  // Fixed label width so values align in a clean column (like a definition list)
  const labelWidth = isCompactInline ? 92 : 130;

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: padY,
      }}
    >
      <Text
        style={{
          color: colors.text.secondary,
          fontSize: labelFont,
          fontWeight: '500',
          width: labelWidth,
          flexShrink: 0,
        }}
        numberOfLines={1}
      >
        {label}
      </Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', flexShrink: 0 }}>
        {children}
      </View>
    </View>
  );
}

function RiskValue({
  riskSummary,
  colors,
  fontSize = 12,
}: {
  riskSummary: RiskSummary;
  colors: any;
  fontSize?: number;
}) {
  const segments: Array<{ count: number; suffix: string; color: string }> = [];
  if (riskSummary.high > 0) {
    segments.push({ count: riskSummary.high, suffix: 'high', color: colors.error });
  }
  if (riskSummary.medium > 0) {
    segments.push({ count: riskSummary.medium, suffix: 'med', color: colors.warning });
  }
  // Always show the low count too (was only shown when there were NO high/med
  // segments, hiding it whenever higher risks existed — Adi 2026-07-23 couldn't
  // see it). Full breakdown reads e.g. "8 high · 5 med · 2 low".
  if (riskSummary.low > 0) {
    segments.push({ count: riskSummary.low, suffix: 'low', color: colors.success });
  }

  if (segments.length === 0) {
    return (
      <Text style={{ color: colors.success, fontSize, fontWeight: '700' }} numberOfLines={1}>
        None
      </Text>
    );
  }

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', flexShrink: 1 }}>
      {segments.map((seg, i) => (
        <React.Fragment key={`${seg.suffix}-${i}`}>
          {i > 0 && (
            <Text style={{ color: colors.text.tertiary, fontSize, marginHorizontal: 4 }}>·</Text>
          )}
          <Text
            style={{ color: seg.color, fontSize, fontWeight: '700' }}
            numberOfLines={1}
          >
            {seg.count} {seg.suffix}
          </Text>
        </React.Fragment>
      ))}
    </View>
  );
}

function SimpleValue({
  text,
  color,
  colors,
  fontSize = 12,
}: {
  text: string;
  color?: string;
  colors: any;
  fontSize?: number;
}) {
  return (
    <Text
      style={{
        color: color ?? colors.text.primary,
        fontSize,
        fontWeight: '700',
        flexShrink: 1,
      }}
      numberOfLines={1}
    >
      {text}
    </Text>
  );
}

export function SubScoreRows({
  ventureLabel,
  ventureScore,
  evidenceLabel,
  evidenceConfidence,
  evidenceSourceFlavor,
  dealReadinessPercent,
  dealReadinessPresent,
  dealReadinessTotal,
  riskSummary,
  density = 'default',
  abbreviatedLabels = false,
  layout = 'inline',
}: SubScoreRowsProps) {
  const colors = useThemeColors();

  const ventureValue =
    ventureLabel && ventureScore !== undefined
      ? `${capitalizeLabel(ventureLabel as string)} (${ventureScore})`
      : ventureLabel
        ? capitalizeLabel(ventureLabel as string)
        : '—';

  const dealValue =
    dealReadinessPercent !== undefined &&
    dealReadinessPresent !== undefined &&
    dealReadinessTotal !== undefined
      ? `${dealReadinessPercent}% (${dealReadinessPresent}/${dealReadinessTotal})`
      : dealReadinessPercent !== undefined
        ? `${dealReadinessPercent}%`
        : '—';

  // Evidence display — prefer confidence numeric ("Strong · 87%" with optional flavor),
  // fall back to legacy 3-tier label when confidence is not provided.
  const hasConfidence = evidenceConfidence !== undefined && evidenceConfidence > 0;
  const evidenceValueText = hasConfidence
    ? `${confidenceBandLabel(evidenceConfidence!)} · ${Math.round(evidenceConfidence!)}%`
    : evidenceLabel ?? '—';
  const evidenceColor = hasConfidence
    ? confidenceBandColor(evidenceConfidence!, colors)
    : evidenceLabel
      ? evidenceThemeColor(evidenceLabel, colors)
      : colors.text.tertiary;

  const ventureColor = ventureLabel
    ? labelThemeColor(ventureLabel as string, colors)
    : colors.text.secondary;

  const labels = abbreviatedLabels
    ? { venture: 'Venture', evidence: 'Evidence', deal: 'Deal Ready', risk: 'Risk' }
    : { venture: 'Venture Quality', evidence: 'Evidence', deal: 'Deal Readiness', risk: 'Risks' };

  const isCompactInline = layout === 'inline' && density === 'compact';
  const valueFont = isCompactInline ? 10.5 : 12;

  return (
    <View>
      <Row label={labels.venture} density={density} layout={layout} colors={colors}>
        <SimpleValue text={ventureValue} color={ventureColor} colors={colors} fontSize={valueFont} />
      </Row>
      <Row label={labels.evidence} density={density} layout={layout} colors={colors}>
        <View style={{ flexDirection: 'column', alignItems: 'flex-start', flexShrink: 1 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', flexShrink: 1 }}>
            <SimpleValue text={evidenceValueText} color={evidenceColor} colors={colors} fontSize={valueFont} />
            {/* Web: source flavor inline with a · separator. */}
            {evidenceSourceFlavor && isWeb && (
              <>
                <Text
                  style={{
                    color: colors.text.tertiary,
                    fontSize: valueFont,
                    marginHorizontal: 4,
                  }}
                >
                  ·
                </Text>
                <Text
                  style={{
                    color: colors.text.tertiary,
                    fontSize: Math.max(9, valueFont - 1),
                    fontWeight: '500',
                    flexShrink: 1,
                  }}
                  numberOfLines={1}
                >
                  {evidenceSourceFlavor}
                </Text>
              </>
            )}
          </View>
          {/* Mobile: source flavor on a second line below confidence. */}
          {evidenceSourceFlavor && !isWeb && (
            <Text
              style={{
                color: colors.text.tertiary,
                fontSize: Math.max(9, valueFont - 1.5),
                fontWeight: '500',
                marginTop: 1,
              }}
              numberOfLines={1}
            >
              {evidenceSourceFlavor}
            </Text>
          )}
        </View>
      </Row>
      <Row label={labels.risk} density={density} layout={layout} colors={colors}>
        {typeof riskSummary === 'object' && riskSummary !== null ? (
          <RiskValue riskSummary={riskSummary} colors={colors} fontSize={valueFont} />
        ) : (
          <SimpleValue text={typeof riskSummary === 'string' ? riskSummary : '—'} colors={colors} fontSize={valueFont} />
        )}
      </Row>
      <Row label={labels.deal} density={density} layout={layout} colors={colors}>
        <SimpleValue text={dealValue} colors={colors} fontSize={valueFont} />
      </Row>
    </View>
  );
}
